import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import DiscoverMoreSection from "@/components/DiscoverMoreSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { HowItWorksSection } from "@/components/ui/how-it-works-section";
import { ImageAutoSlider } from "@/components/ui/image-auto-slider";
import { Palette, Globe, Link2, Wand2, Edit3, Play, MoveHorizontal, Languages, Megaphone } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { navigateToEnterpriseDemo } from "@/utils/navigation";
import CTASection from "@/components/CTASection";

function VisualLabPage() {
  const navigate = useNavigate();

  const why = [{
    icon: Link2,
    text: "Creation or last-mile editing for publish-ready assets"
  }, {
    icon: Palette,
    text: "Visual Vault styles and assets always on-hand"
  }, {
    icon: Globe,
    text: "Built-in AI capabilities to localize for any market"
  }];
  const powers = [{
    title: "Generate",
    desc: "Visualize ideas into ready-to-use HQ images.",
    icon: Wand2
  }, {
    title: "Create",
    desc: "Produce snackable, platform-ready videos.",
    icon: Play
  }, {
    title: "Edit",
    desc: "Format, design, translate into on-brand creatives.",
    icon: Edit3
  }];
  const _workflows = [{
    title: "Resize (40+ formats)",
    desc: "Auto-fit carousels, stories, banners, and more.",
    icon: MoveHorizontal
  }, {
    title: "Translate (40 languages)",
    desc: "Localize text and visuals in a click.",
    icon: Languages
  }, {
    title: "Optimize per channel",
    desc: "Aspect ratios, safe zones, and specs handled.",
    icon: Megaphone
  }];

  const galleryImages = [
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/3f698375-088f-4073-a6c4-425df18d23c6/5_+Buah.jpg?content-type=image%2Fpng", // Fruit arrangement
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/84788af4-ce53-4665-a2e7-7575dcebf799/2_hamster+v.jpg?content-type=image%2Fjpeg", // Hamster
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/4e0a6e5e-c36b-461e-a5ae-16b618069e5e/4.phone.gif?content-type=image%2Fgif",
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/d3c76821-72cc-4e82-835e-d92e8307eb9f/6.Cat.gif?content-type=image%2Fgif",
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/d7aaa64b-a386-4cac-88ab-6f2ac9a7eec8/1_panda.gif?content-type=image%2Fgif",
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/47c9903b-3e9c-499f-a18a-b641b2a6bcaa/3.Sotong.gif?content-type=image%2Fgif",
    "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/50441768-3b1a-454c-881d-3d9024147633/7_robot.gif?content-type=image%2Fgif"
  ];

  return <div className="min-h-screen bg-background mt-24 md:mt-0">
    <Navigation />

    <div style={{
      '--brand': '20 90% 55%',
      '--brand-foreground': '25 95% 65%'
    } as React.CSSProperties}>
      <ContainerScroll titleComponent={<>
        <div className="mb-4">
          <Badge className="bg-primary/10 text-primary border-primary/20">
            Visual Lab
          </Badge>
        </div>
        <h1 className="text-4xl font-semibold text-black dark:text-white mb-4">
        Create, edit, polish, perfect: Visual Lab for effortless brand-first visuals
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          From social ads to product images, your brand style is built in from the first pixel.
        </p>
        <div className="flex justify-center mt-8">
          <Button id="visual-lab-book-demo-button" className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors h-12" onClick={() => navigateToEnterpriseDemo(navigate)}>
            Book a Visual Lab Demo
          </Button>
        </div>
      </>}>
        <img src="/lovable-uploads/615b6300-1f3b-4ff6-b674-a7761e0fe271.jpg" width={1651} height={900} alt="Visual Lab interface" className="mx-auto rounded-2xl object-cover h-full object-center" draggable={false} />
      </ContainerScroll>
    </div>

    {/* Section 1: Why Visual Lab */}
    <section className="py-16 bg-muted/30 mt-[135px]">
      <div className="container mx-auto px-6 grid md:grid-cols-3 gap-6">
        {why.map((w, i) => <Card key={i} className="h-full">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 grid place-items-center">
              <w.icon className="w-5 h-5" />
            </div>
            <div className="font-medium">{w.text}</div>
          </CardContent>
        </Card>)}
      </div>
    </section>

    {/* Section 2: How Visual Lab Works */}
    <HowItWorksSection
      title="How Visual Lab Works"
      steps={[
        "Generate or create a visual",
        "Edit with built-in AI tools and other functionalities",
        "Add in brand assets like templates, logos, etc",
        "Translate and localize content",
        "Smart resize for multiple channels",
        "Export or publish brand-ready assets"
      ]}
      image={{
        src: "/lovable-uploads/7b992933-f1c5-40c9-b652-be4097aa99d8.jpg",
        alt: "Creative design interface showing fonts and color palette with illustrated characters on skateboards"
      }}
    />

    {/* Section 3: Three Core Powers */}
    <section className="py-16">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Three Core Powers</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {powers.map((p, i) => <Card key={i} className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary border border-primary/20 grid place-items-center">
                <p.icon className="w-5 h-5" />
              </div>
              <h3 className="text-xl font-semibold">{p.title}</h3>
            </div>
            <p className="text-muted-foreground">{p.desc}</p>
            {i === 0 && (
              <div className="mt-4 aspect-square rounded-lg overflow-hidden">
                <img
                  src="https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/a2f3898b-9273-45b0-b7cb-e3cfbdab4711/vb+1.jpg?content-type=image%2Fjpeg"
                  alt="Generate visual content example"
                  className="w-full h-full object-cover"
                  width={385}
                  height={385}
                />
              </div>
            )}
            {i === 1 && (
              <div className="mt-4 aspect-square rounded-lg overflow-hidden">
                <img
                  src="https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/cc9fda85-3b92-49a9-9fc6-b3cd4a274a81/vb+2.jpg?content-type=image%2Fjpeg"
                  alt="Edit visual content example"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {i === 2 && (
              <div className="mt-4 aspect-square rounded-lg overflow-hidden">
                <img
                  src="https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/7c570372-4317-43de-87fb-2849c50d8d17/rocket-ezgif.com-video-to-gif-converter.gif?content-type=image%2Fgif"
                  alt="Publish content example"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </Card>)}
        </div>
      </div>
    </section>

    {/* Final CTA with Gallery */}
    <section className="py-20 bg-gradient-to-r from-primary/10 via-primary/5 to-background text-center">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl md:text-5xl font-bold mb-8">Your visuals, your way.</h2>

        {/* Image Gallery Slider */}
        <div className="mb-12">
          <ImageAutoSlider images={galleryImages} />
        </div>

        <Button size="lg" onClick={() => navigateToEnterpriseDemo(navigate)}>
          Book a Visual Lab tour
        </Button>
      </div>
    </section>

    <CTASection title="Know a creator who loves eye-catching visuals?" subtitle="Invite them to explore Visual Lab and they’ll get bonus signup credits to kick off their next project." buttonText="Tell a friend" buttonLink="/tellafriend" />

    <DiscoverMoreSection currentPage="visual-lab" />

    <FooterWithNewsletter />
  </div>;
}

export default VisualLabPage;
